import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { useAiService } from '@/services/ai/useAiService'
import { useAuthStore } from '@/stores/useAuthStore'

type RefreshCreditsOptions = {
  force?: boolean
}

export const useAiCreditsStore = defineStore('ai-credits', () => {
  const aiService = useAiService()
  const authStore = useAuthStore()

  const balanceCredits = ref<number | null>(null)
  const isLoading = ref(false)
  const loadedUserId = ref<string | null>(null)

  const currentUserId = computed(() => authStore.user?.id ?? null)
  const hasLoadedBalance = computed(
    () => loadedUserId.value !== null && loadedUserId.value === currentUserId.value,
  )

  const clearCredits = () => {
    balanceCredits.value = null
    loadedUserId.value = null
  }

  const refreshCredits = async (options: RefreshCreditsOptions = {}) => {
    const userId = currentUserId.value
    if (!userId) {
      clearCredits()
      return null
    }

    if (!options.force && hasLoadedBalance.value && balanceCredits.value !== null) {
      return balanceCredits.value
    }

    try {
      isLoading.value = true
      const response = await aiService.getPlatformCredits()
      balanceCredits.value = Number(response.token_balance)
      loadedUserId.value = userId
      return balanceCredits.value
    } catch (error) {
      if (!hasLoadedBalance.value) clearCredits()
      throw error
    } finally {
      isLoading.value = false
    }
  }

  return { balanceCredits, isLoading, refreshCredits, clearCredits }
})
